import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { ApiError, getApi, ZOD_PARSE_ERROR } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { API_OFFBOARDING_QUESTIONNAIRE } from "../../../../../config/Endpoints.js";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { sqeOffboardingApi } from "../api/sqe-offboarding-api.ts";
import {
    OffboardingQuestion,
    OffboardingQuestionSchema,
    OffboardingSectionType,
} from "../schemas/index.ts";

export function useOffboardingQuestionnaire(
    offboardingId: number | null,
    sectionType: OffboardingSectionType,
) {
    const [questions, setQuestions] = useState<OffboardingQuestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSqe = useSqeLabs();

    const fetchQuestions = useCallback(
        async (signal?: AbortSignal) => {
            if (offboardingId == null) {
                setQuestions([]);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                if (isSqe) {
                    setQuestions(await sqeOffboardingApi.getQuestionnaire(offboardingId, sectionType, signal));
                    return;
                }
                const url = `${API_OFFBOARDING_QUESTIONNAIRE}/${offboardingId}/${sectionType}`;
                const { data } = await getApi<OffboardingQuestion[]>(
                    url,
                    accessToken(),
                    "json",
                    signal,
                );

                if (!data) {
                    setQuestions([]);
                } else {
                    const parse = z.array(OffboardingQuestionSchema).safeParse(data);

                    if (!parse.success) {
                        setQuestions([]);
                        console.error("Parse error:", parse.error);
                        setError(ZOD_PARSE_ERROR);
                    } else {
                        setQuestions(parse.data);
                    }
                }
            } catch (e) {
                const msg =
                    e instanceof ApiError
                        ? e.message
                        : "Failed to load questionnaire.";
                setError(msg);
                setQuestions([]);
            } finally {
                setLoading(false);
            }
        },
        [offboardingId, sectionType, isSqe],
    );

    useEffect(() => {
        const ac = new AbortController();
        void fetchQuestions(ac.signal);
        return () => ac.abort();
    }, [fetchQuestions]);

    return { questions, loading, error, refetch: fetchQuestions };
}
